import { sb } from '../supabase';
import { summarizeOutcomes } from './claimCampaign';

// Data layer for the admin distribution panel: pick factories, create one
// request invite each, then send the approved template to the ones that have
// an invite.
//
// ⚠️ THIS HEADER USED TO READ "NOTHING HERE SENDS ANYTHING", and it was wrong
// from the moment sendRequestRfq was added below on 2026-09-18. Corrected
// 2026-09-19. Creating and sending are still TWO ACTS — createRequestInvites
// mints slugs and sends nothing, sendRequestRfq sends and mints nothing — but
// sending does now happen from this file.
//
// The ACCEPTED RISK in docs/TECH_DEBT.md (2026-09-17) — claim_factory_by_slug
// is ungated, so a slug plus any contact the holder controls can take a catalog
// factory — was written when it rested on distribution being manual and
// one-to-one. What changed is the risk, not the appetite: register_factory_invite
// stopped binding ownership on 2026-09-17 (migration 20260917130000), so opening
// a link reads a request instead of acquiring a factory. The soft cap and the
// mandatory dry run are what remain of the caution.
//
// WHATSAPP LEAVES BY ONE PATH ONLY: sendRequestRfq, on the approved factory_rfq
// template, logged per invite in wa_messages. The per-invite wa.me button that
// used to sit beside it was removed on 2026-09-19 — a hand-off writes no row,
// so the duplicate guard cannot see it and the log stops being a record of what
// factories actually received.
//
// summarizeOutcomes is imported from claimCampaign rather than copied: it is a
// five-line pure count-by-outcome, and two drifting definitions of "how many
// succeeded" is worse than the coupling. The SELECTION MODEL is deliberately
// NOT shared — see the note in the panel component.

/**
 * Soft cap on one create-invites batch.
 *
 * Not a technical limit — the RPC will take any array. It is a judgement about
 * how many live ownership-bearing slugs should be outstanding at once, because
 * until the identity match ships an invite slug IS a bearer token for that
 * factory. Ten is the agreed number (Majeed, 2026-09-17). The dialog explains
 * the reason rather than only the count: a cap whose rationale is invisible
 * gets raised by the next person who finds it inconvenient.
 */
export const DISTRIBUTE_SOFT_CAP = 10;

/**
 * Is this request still collecting quotes?
 *
 * ⚠️ MIRRORS public.request_is_offerable(uuid), which is the authority and the
 * enforcement — every push path checks it server-side and returns
 * `not_offerable` or raises REQUEST_NOT_OFFERABLE. This copy exists ONLY so the
 * console can stop ASKING a question whose answer is already no: the buttons go
 * disabled rather than firing an RPC that will refuse them.
 *
 * POSITIVE TEST, naming the states that ARE offerable rather than excluding the
 * ones that are not (golden rule 12). A status invented later is closed here by
 * default, which is the safe direction: the worst case is a greyed button on a
 * live request, not a paid WhatsApp about a dead one.
 *
 * Returns false for a missing/loading request too — the buttons stay disabled
 * until the row has actually arrived, which is also the honest answer.
 */
export function isRequestOfferable(request) {
  return ['open', 'offers_received'].includes(String(request?.status || '').toLowerCase());
}

// 'not_found' is a real sentinel in factory_directory, not a hypothetical — the
// same nf() guard is in consoleQuotations.js and InviteModal.jsx.
const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');

/**
 * The URL button's base, as configured AT META on the approved template.
 *
 * We send the slug and nothing else — this string is never transmitted. It
 * exists so the send preview can show the admin the whole link the factory will
 * see, and it is deliberately NOT window.location.origin: on localhost or a
 * preview deploy that would render a link nobody is ever sent, which is the one
 * thing a preview must not do.
 *
 * If the base is ever changed at Meta, this line is wrong and the preview lies.
 * Nothing here can detect that — only reading the template in the console can.
 */
export const RFQ_BUTTON_BASE = 'https://maabar.io/f/';

/**
 * {{2}} of the factory_rfq template: what the buyer is sourcing.
 *
 * ⚠️ THIS MIRRORS SQL, and the SQL is the authority. admin_send_request_rfq
 * (supabase/migrations/20260918113412_admin_send_request_rfq.sql:90-97) resolves
 * it as:
 *
 *   coalesce(nullif(trim(r.title_en), ''), nullif(trim(r.title_zh), ''),
 *            nullif(trim(r.title_ar), ''))
 *
 * English first, because the template is approved in English only.
 *
 * THE TWO EXPRESSIONS MUST MOVE TOGETHER. A preview that resolves {{2}}
 * differently from the RPC shows the admin a message that is not the one that
 * gets sent — worse than showing no preview at all, because it will be
 * believed. This is the isReviewable()/fetchReadyCount() trap in
 * src/lib/consoleVerification.js, in a second place.
 *
 * nf() is deliberately NOT used: it also strips the literal 'not_found', which
 * is a factory_directory sentinel and has no meaning on requests.title_*. Using
 * it here would be a third behaviour, agreeing with neither the SQL nor itself.
 *
 * Returns '' when all three are blank — the RPC returns `no_product` for every
 * factory in that run and queues nothing.
 */
const trimmed = (v) => String(v ?? '').trim();

export function rfqProductValue(request) {
  return trimmed(request?.title_en) || trimmed(request?.title_zh) || trimmed(request?.title_ar);
}

export function factoryDisplayName(f) {
  return nf(f?.company_name_latin) || nf(f?.company_name) || '';
}

/** A factory with no usable email cannot get an invite: factory_email is NOT NULL. */
export function factoryHasEmail(f) {
  return !!nf(f?.email);
}

/**
 * PostgREST's `or=` takes a COMMA-SEPARATED list inside parentheses, so a search
 * term containing , ( ) or a backslash does not fail — it silently changes which
 * filter is applied. That is not SQL injection (PostgREST parameterises the
 * values), but it is a correctness hole: searching for "ACME, Ltd" would parse
 * as two unrelated conditions and return the wrong rows with no error.
 *
 * % and _ are stripped for the same class of reason: they are ilike wildcards,
 * and a user typing them means them literally.
 */
const sanitizeTerm = (v) => String(v || '').replace(/[,()\\%_*]/g, ' ').trim();

/**
 * Active factories matching a search term and/or category, for the picker.
 *
 * Reads factory_directory directly rather than through an RPC: admins already
 * have full SELECT via the factory_directory_admin_all policy, which is how
 * catalogImport.js reads it too. A non-admin gets zero rows from RLS rather than
 * an error, so the panel degrades to "no factories" instead of breaking — worth
 * knowing when debugging an empty picker.
 *
 * linked_supplier_id is selected so the row can show whether a factory is
 * already claimed. It is NOT filtered on: a claimed factory is a perfectly good
 * distribution target — arguably the best one, since a claimed factory has an
 * owner who can actually reply.
 */
// The cap was 60 until 2026-10-12, and «كل المصانع» showed the first 60 of
// 108 alphabetically — the other 48 were unreachable except by typing their
// name, which is how staff concluded "not every factory shows up". 1000 is a
// ceiling, not a page size: the whole catalogue is one screenful of rows.
export const DISTRIBUTE_FACTORY_LIMIT = 1000;

export async function fetchDistributableFactories({ q = '', categoryCodes = null, limit = DISTRIBUTE_FACTORY_LIMIT } = {}) {
  let query = sb
    .from('factory_directory')
    .select('id, company_name, company_name_latin, category, city, country, email, phone, wa_phone_e164, linked_supplier_id')
    .eq('is_active', true)
    .order('company_name', { ascending: true })
    .limit(limit);

  // An ARRAY of real codes, not one code: the console's category chips are
  // FACTORY_DISPLAY_CATEGORIES, each of which groups several underlying
  // `factory_directory.category` values (codesForDisplayCategory). Filtering on a
  // single code would quietly return a subset of the chip the admin pressed.
  // An empty array means the catch-all chip — no filter, not "match nothing".
  if (Array.isArray(categoryCodes) && categoryCodes.length) {
    query = query.in('category', categoryCodes);
  }

  const term = sanitizeTerm(q);
  if (term) {
    query = query.or(
      `company_name.ilike.%${term}%,company_name_latin.ilike.%${term}%,city.ilike.%${term}%`,
    );
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Every invite already created for one request, newest first, with its factory.
 *
 * Two queries rather than a PostgREST embed: the embed syntax needs the foreign
 * key CONSTRAINT name, and naming a constraint this code has never verified is
 * how a screen breaks on a rename it never mentions. consoleQuotations.js joins
 * the same way for the same reason.
 */
export async function fetchRequestInvites(requestId) {
  if (!requestId) return [];

  const { data: invites, error } = await sb
    .from('request_factory_invites')
    .select('id, factory_id, slug, factory_email, status, opened_at, registered_at, offer_submitted_at, created_at, expires_at')
    .eq('request_id', requestId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  if (!invites || !invites.length) return [];

  const ids = [...new Set(invites.map((i) => i.factory_id).filter(Boolean))];
  let facBy = {};
  if (ids.length) {
    const { data: facs } = await sb
      .from('factory_directory')
      .select('id, company_name, company_name_latin, email, phone, wa_phone_e164, linked_supplier_id')
      .in('id', ids);
    (facs || []).forEach((f) => { facBy[f.id] = f; });
  }

  return invites.map((i) => ({ ...i, factory: facBy[i.factory_id] || null }));
}

/**
 * Create one invite per factory for a request. ALWAYS call with dryRun first.
 *
 * The dry run walks the same server-side code path as the write, so the
 * confirmation dialog cannot quote a number the execution then contradicts —
 * the client must never re-derive "who gets an invite". Same contract as
 * sendClaimInvites/markPriorContactBulk, and for the same reason: the picker's
 * view of a factory can be minutes old.
 *
 * Idempotent on (request_id, factory_id): a factory that already has an invite
 * comes back as `existing` carrying the slug already in circulation, never a
 * second one. A factory holding an older link would otherwise find it orphaned.
 */
export async function createRequestInvites(requestId, factoryIds, { dryRun = false } = {}) {
  const { data, error } = await sb.rpc('admin_create_request_invites', {
    p_request_id: requestId,
    p_factory_ids: factoryIds || [],
    p_dry_run: dryRun,
  });
  if (error) throw error;
  return data || [];
}

/**
 * Send the approved factory_rfq template to factories that ALREADY have an
 * invite for this request. It creates nothing: the invite row owns the slug the
 * template's button carries, so "create the link" and "send the link" stay two
 * acts and a mis-click on the second cannot mint the first.
 *
 * Dry run FIRST, always, and not as ceremony. The sender drops a message with no
 * consent and one with no sendable number AFTER it is queued and without saying
 * so; production carries 112 factories, 91 with a number and 87 with consent. An
 * admin told "sent to 30" would be wrong about a third of them, so the RPC makes
 * both checks itself and reports them per factory before anything is written.
 */
export async function sendRequestRfq(requestId, factoryIds, { dryRun = true } = {}) {
  const { data, error } = await sb.rpc('admin_send_request_rfq', {
    p_request_id: requestId,
    p_factory_ids: factoryIds || [],
    p_dry_run: dryRun,
  });
  if (error) throw error;
  return data || [];
}

export { summarizeOutcomes };

/**
 * Both tenses on the same key, exactly as OUTCOME_LABEL in claimCampaign.js.
 *
 * The preview dialog and the result dialog are the SAME dialog, so "will be
 * created" left standing after the write would be a claim about the future made
 * about something already done. The server returns one outcome value for both
 * runs on purpose; the tense is the client's to render.
 *
 * Arabic + English only: this is the admin console, which golden rule 1 names as
 * the one web-only surface, and every screen around it is ar/en.
 */
export const OUTCOME_LABEL = {
  created: {
    ar: 'ستُنشأ دعوة', en: 'Invite will be created',
    arDone: 'أُنشئت', enDone: 'Created',
  },
  existing: {
    ar: 'موجودة مسبقاً — سيُعاد نفس الرابط', en: 'Already exists — same link returned',
    arDone: 'موجودة مسبقاً', enDone: 'Already existed',
  },
  no_email: {
    ar: 'متخطّى — لا بريد للمصنع', en: 'Skipped — no email on file',
  },
  inactive: {
    ar: 'متخطّى — مصنع غير نشط', en: 'Skipped — inactive factory',
  },
  not_found: {
    ar: 'متخطّى — مصنع غير موجود', en: 'Skipped — factory not found',
  },
  slug_exhausted: {
    ar: 'فشل — تعذّر توليد رابط', en: 'Failed — could not allocate a link',
  },

  // ── admin_send_request_rfq ────────────────────────────────────────────────
  // Shared with the invite outcomes on purpose: one dialog renders both, and a
  // second vocabulary for the same act would be two things to keep in step.
  enqueued: {
    ar: 'سيُرسل واتساب', en: 'WhatsApp will be sent',
    arDone: 'أُدرج في الطابور', enDone: 'Queued',
  },
  duplicate: {
    ar: 'أُرسل سابقاً — لن يتكرّر', en: 'Already sent — will not repeat',
    arDone: 'أُرسل سابقاً', enDone: 'Already sent',
  },
  no_invite: {
    ar: 'متخطّى — لا دعوة لهذا المصنع', en: 'Skipped — no invite for this factory',
  },
  expired: {
    ar: 'متخطّى — الدعوة منتهية', en: 'Skipped — invite expired',
  },
  // The two the sender would otherwise swallow silently.
  no_phone: {
    ar: 'متخطّى — لا رقم واتساب صالح', en: 'Skipped — no valid WhatsApp number',
  },
  no_consent: {
    ar: 'متخطّى — لم يوافق على استقبال الرسائل', en: 'Skipped — has not opted in',
  },
  no_product: {
    ar: 'فشل — الطلب بلا عنوان يوضع في الرسالة', en: 'Failed — the request has no title to put in the message',
  },
  // The request itself is no longer collecting — cancelled, closed, or already
  // paid. Not a property of any factory, which is why every row carries it at
  // once: the RPCs return it before they look a single factory up.
  not_offerable: {
    ar: 'متوقّف — الطلب لم يعد يستقبل عروضاً', en: 'Blocked — the request is no longer collecting offers',
  },
};

export function outcomeLabel(outcome, isAr, done = false) {
  const e = OUTCOME_LABEL[outcome];
  if (!e) return outcome || '';
  if (done) return (isAr ? e.arDone : e.enDone) || (isAr ? e.ar : e.en);
  return isAr ? e.ar : e.en;
}

/** Outcomes that produced nothing — worth counting separately in the dialog. */
export const FAILED_OUTCOMES = [
  'no_email', 'inactive', 'not_found', 'slug_exhausted',
  'no_invite', 'expired', 'no_phone', 'no_consent', 'no_product',
  'not_offerable',
];

/**
 * Invite status for the panel's status column, read from the columns that
 * already exist. No schema change: status is the table's own check-constrained
 * vocabulary (sent / opened / registered / offer_submitted) and the three
 * timestamps are the table's own.
 *
 * Kept distinct from the STATUS map in ConsoleQuotations, which describes a
 * REQUEST (waiting / replied / closed). One word meaning two things on one
 * screen is the confusion worth avoiding here.
 */
export const INVITE_STATUS = {
  sent:            { ar: 'أُرسلت',        en: 'Sent',          cls: 'neutral' },
  opened:          { ar: 'فُتحت',         en: 'Opened',        cls: 'info' },
  registered:      { ar: 'سجّل المصنع',   en: 'Registered',    cls: 'warn' },
  offer_submitted: { ar: 'قدّم عرضاً',    en: 'Offer submitted', cls: 'ok' },
};

export function inviteStatusLabel(row, isAr) {
  const s = INVITE_STATUS[row?.status];
  return s ? (isAr ? s.ar : s.en) : (row?.status || '');
}

export function isInviteExpired(row) {
  return !!row?.expires_at && new Date(row.expires_at).getTime() <= Date.now();
}


// ── Self-registered suppliers, in the same picker (2026-10-12) ──────────────
//
// Staff used to route suppliers in a separate panel (RoutePanel) and invite
// factories here, and the split read as "the distribute screen does not show
// every supplier": its «عندهم حساب» group listed only CLAIMED FACTORIES (two),
// while ~30 self-registered suppliers lived one card further down. Both now
// live in this one picker; each keeps its own write path.

/**
 * Every operational supplier, with this request's routing rows and the
 * supplier ids already represented by a claimed factory (those appear in the
 * claimed-factory group, so they are left out of the supplier group).
 *
 * "Operational" mirrors public.is_operational_supplier(): role supplier and
 * status not rejected/inactive. That function is stated as an exclusion, so
 * this is too; admin_route_request_to_suppliers re-applies it server-side.
 */
export async function fetchDistributionSuppliers(requestId) {
  const [sup, rt, linked] = await Promise.all([
    sb.from('profiles')
      .select('id, company_name, company_name_latin, full_name, status, speciality, city, country')
      .eq('role', 'supplier'),
    sb.from('request_routing')
      .select('supplier_id, source, created_at')
      .eq('request_id', requestId),
    sb.from('factory_directory')
      .select('linked_supplier_id')
      .not('linked_supplier_id', 'is', null),
  ]);
  // Security rule 4 on our own backend: a swallowed routing read would show
  // "nobody has it" about a request that already reached every supplier.
  if (sup.error) throw sup.error;
  if (rt.error) throw rt.error;
  if (linked.error) throw linked.error;
  const suppliers = (sup.data || []).filter(
    (s) => !['rejected', 'inactive'].includes(String(s.status || '').toLowerCase()),
  );
  return {
    suppliers,
    routing: rt.data || [],
    linkedSupplierIds: new Set((linked.data || []).map((r) => r.linked_supplier_id)),
  };
}

/** Category of every active factory — for the counts on the chips. */
export async function fetchFactoryCategories() {
  const { data, error } = await sb.from('factory_directory').select('category').eq('is_active', true);
  if (error) throw error;
  return (data || []).map((r) => r.category);
}

/** Route this request to suppliers. Returns how many routes were ADDED. */
export async function routeRequestToSuppliers(requestId, supplierIds) {
  const { data, error } = await sb.rpc('admin_route_request_to_suppliers', {
    p_request_id: requestId, p_supplier_ids: supplierIds, p_note: null,
  });
  if (error) throw error;
  return Number(data) || 0;
}

/** Remove an ADMIN route. A public_link / open_pool route is not removed. */
export async function unrouteRequest(requestId, supplierId) {
  const { data, error } = await sb.rpc('admin_unroute_request', { p_request_id: requestId, p_supplier_id: supplierId });
  if (error) throw error;
  return !!data;
}

export const SUPPLIER_VERIFIED_STATUSES = ['verified', 'approved', 'active'];
export const isVerifiedSupplier = (s) => SUPPLIER_VERIFIED_STATUSES.includes(String(s?.status || '').toLowerCase());

export function supplierDisplayName(s) {
  const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
  return nf(s?.company_name) || nf(s?.company_name_latin) || nf(s?.full_name) || String(s?.id || '').slice(0, 8);
}
